//Libs
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import { z } from "zod";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

//Local
import BaseModal from "../BaseModal";
import * as strings from "../../strings";
import { ExistingZone } from "../../models/zone";
import { logger as devLogger } from "../../logging";
import { ExistingSiteKeyUserPermissions } from "../../models/site-key-user-permissions";
import { ErrorMessage } from "../ErrorMessage";
import BaseButtonSecondary from "../BaseButtonSecondary";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseInputSelect from "../BaseInputSelect";
import StyledMessage from "../StyledMessage";
import BaseInputText from "../BaseInputText";
import BaseInputTextArea from "../BaseInputTextArea";
import { ExistingSiteKey } from "../../models/site-key";
import { SketchPicker } from "react-color";
import ButtonColored from "../ButtonColored";
import { ConvertArrayToString } from "../../assets/js/HandleOptions";

interface Props {
  /* DATA */
  isDialogOpen: boolean;
  zoneDoc: ExistingZone | null;
  userPermissions: ExistingSiteKeyUserPermissions;
  siteKeyDoc: ExistingSiteKey;
  /* FUNCTIONS */
  closeDialog: () => void;
  handleSaveNewZone: (newZone: ZoneFormState) => Promise<void>;
  handleEditZone: (updateZone: ZoneFormState) => Promise<void>;
  handleDeleteZone: (zoneID: ExistingZone["id"]) => Promise<void>;
}

const ZoneFormSchema = z.object({
  name: z.string().min(1).max(200),
  element: z.enum(["dot", "background"]),
  property: z.enum(["zipCode", "title-contains"]),
  values: z.string().min(0).max(4000),
  color: z.string().min(1).max(200),
});

export type ZoneFormState = z.infer<typeof ZoneFormSchema>;

export default function AddEditZoneDialog({
  closeDialog,
  handleSaveNewZone,
  handleEditZone,
  handleDeleteZone,
  siteKeyDoc,
  isDialogOpen,
  userPermissions,
  zoneDoc,
}: Props) {
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [selectedColor, setSelectedColor] = useState<string>("#000000");
  const [displayColorPicker, setDisplayColorPicker] = useState<boolean>(false);

  useEffect(() => {
    if (zoneDoc) {
      setSelectedColor(zoneDoc.color);
    }
  }, [zoneDoc]);

  const ZoneFormDefaultValues: ZoneFormState = useMemo(() => {
    if (zoneDoc) {
      return {
        name: zoneDoc.name,
        element: zoneDoc.element,
        property: zoneDoc.property,
        values: ConvertArrayToString(zoneDoc.values),
        color: zoneDoc.color,
      };
    } else {
      return {
        name: "",
        element: "dot",
        property: "zipCode",
        values: "",
        color: "#000000",
      };
    }
  }, [zoneDoc]);

  const {
    control,
    formState: { errors, isSubmitting },
    reset,
    handleSubmit,
  } = useForm<ZoneFormState>({
    defaultValues: ZoneFormDefaultValues,
    resolver: zodResolver(ZoneFormSchema),
    mode: "onChange",
  });

  /* fn that handle all the states that needs to be reset when the dialog is closed */
  function closeAndReset() {
    reset();
    setDisplayError(false);
    closeDialog();
  }

  useEffect(() => {
    reset(ZoneFormDefaultValues);
  }, [ZoneFormDefaultValues, reset]);

  const onSubmit: SubmitHandler<ZoneFormState> = async (formValues) => {
    const partialDoc: ZoneFormState = {
      ...formValues,
      color: selectedColor,
    };

    if (zoneDoc != null) {
      try {
        await handleEditZone(partialDoc);
        // Close dialog if successful.
        closeAndReset();
      } catch (error) {
        setDisplayError(true);
        devLogger.error(error);
      }
    } else {
      try {
        await handleSaveNewZone(partialDoc);
        // Close dialog if successful.
        closeAndReset();
      } catch (error) {
        setDisplayError(true);
        devLogger.error(error);
      }
    }
  };

  const zoneHeader = (
    <div className="mb-4 flex w-full items-center justify-between rounded-t-lg bg-primary p-8 text-left text-white ">
      <h1 className="inline-flex items-center text-xl font-semibold ">
        {zoneDoc ? strings.EDIT_ZONE : strings.ADD_NEW_ZONE}
      </h1>
      <button type="button" onClick={() => closeAndReset()}>
        <XMarkIcon
          aria-label="close new requirement form"
          className="h-6 text-white"
        />
      </button>
    </div>
  );

  const errorMessage = zoneDoc ? (
    <ErrorMessage
      message={strings.ERROR_EDITING_ZONE}
      clearMessage={() => setDisplayError(false)}
    />
  ) : (
    <ErrorMessage
      message={strings.ERROR_CREATING_ZONE}
      clearMessage={() => setDisplayError(false)}
    />
  );

  return (
    <BaseModal
      closeModal={closeAndReset}
      open={isDialogOpen}
      title={zoneHeader}
      parentDivStyles="inline-block transform overflow-hidden  max-w-screen-sm rounded-lg bg-white text-left align-middle shadow-xl transition-all"
    >
      <div className="relative flex flex-col space-y-8 p-8 text-lg">
        <form
          autoComplete="off"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-8"
        >
          {/* Field: Color (color picker that returns HEX color string */}
          <div className="flex flex-row items-center space-x-4">
            <span>Select Zone Color: </span>
            <div className="flex flex-col space-y-2">
              <div
                style={{
                  padding: "5px",
                  background: "#fff",
                  borderRadius: "1px",
                  boxShadow: "0 0 0 1px rgba(0,0,0,.1)",
                  display: "inline-block",
                  cursor: "pointer",
                }}
                onClick={() => setDisplayColorPicker(!displayColorPicker)}
              >
                <div
                  style={{
                    width: "36px",
                    height: "14px",
                    borderRadius: "2px",
                    background: `${selectedColor}`,
                  }}
                />
              </div>
              {displayColorPicker ? (
                <div
                  style={{
                    position: "absolute",
                    zIndex: "2",
                  }}
                >
                  <div
                    style={{
                      position: "fixed",
                      top: "0px",
                      right: "0px",
                      bottom: "0px",
                      left: "0px",
                    }}
                    onClick={() => setDisplayColorPicker(false)}
                  />
                  <SketchPicker
                    className={"absolute z-50"}
                    color={selectedColor}
                    onChange={(color) => setSelectedColor(color.hex)}
                  />
                </div>
              ) : null}
            </div>
          </div>
          {/* Field: Name */}
          <div>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <BaseInputText
                  text="Zone Name"
                  inputName="name"
                  admin={true}
                  required={true}
                  {...field}
                />
              )}
            />
            {errors.name?.message && (
              <div className="mt-2 text-sm">
                <StyledMessage type="error">
                  {{ message: errors.name.message }}
                </StyledMessage>
              </div>
            )}
          </div>

          {/* Field: Values */}
          <div>
            <Controller
              name="values"
              control={control}
              render={({ field }) => (
                <BaseInputTextArea
                  rows={3}
                  text="Values (separated by a comma)"
                  inputName="values"
                  admin={true}
                  required={false}
                  placeholder="Separate values by comma."
                  {...field}
                />
              )}
            />
            {errors.values?.message && (
              <div className="mt-2 text-sm">
                <StyledMessage type="error">
                  {{ message: errors.values.message }}
                </StyledMessage>
              </div>
            )}
          </div>

          {/* Field: Element */}
          <div>
            <Controller
              name="element"
              control={control}
              render={({ field }) => (
                <BaseInputSelect
                  inputName="element"
                  text="Dispatch Board Element"
                  admin={true}
                  required={true}
                  className="capitalize"
                  {...field}
                  value={field.value === null ? "" : field.value}
                  onChange={(event) =>
                    field.onChange(
                      event.target.value === "" ? null : event.target.value,
                    )
                  }
                >
                  {["dot", "background"].map((e) => {
                    return (
                      <option key={e} value={e}>
                        {e}
                      </option>
                    );
                  })}
                </BaseInputSelect>
              )}
            />
            {errors.element?.message && (
              <div className="mt-2 text-sm">
                <StyledMessage type="error">
                  {{ message: errors.element.message }}
                </StyledMessage>
              </div>
            )}
          </div>

          {/* Field: Property */}
          <div>
            <Controller
              name="property"
              control={control}
              render={({ field }) => (
                <BaseInputSelect
                  inputName="property"
                  text="Property"
                  admin={true}
                  required={true}
                  className="capitalize"
                  {...field}
                  value={field.value === null ? "" : field.value}
                  onChange={(event) =>
                    field.onChange(
                      event.target.value === "" ? null : event.target.value,
                    )
                  }
                >
                  {["zipCode", "title-contains"].map((p) => {
                    return (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    );
                  })}
                </BaseInputSelect>
              )}
            />
            {errors.property?.message && (
              <div className="mt-2 text-sm">
                <StyledMessage type="error">
                  {{ message: errors.property.message }}
                </StyledMessage>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex w-full flex-col items-center justify-between gap-4 xs:flex-row">
            {userPermissions.permissions.isSiteAdmin && zoneDoc?.id && (
              <ButtonColored
                className="w-full justify-center uppercase"
                kind="danger"
                type="button"
                onClick={() => handleDeleteZone(zoneDoc.id)}
              >
                {strings.DELETE_ZONE}
              </ButtonColored>
            )}
            <BaseButtonSecondary
              type="button"
              className="w-full justify-center uppercase"
              onClick={closeAndReset}
            >
              {strings.buttons.CANCEL}
            </BaseButtonSecondary>

            <BaseButtonPrimary
              type="submit"
              formNoValidate
              disabled={isSubmitting}
              isBusy={isSubmitting}
              busyText={strings.buttons.BUSY_SAVING}
              className="w-full justify-center uppercase"
            >
              {strings.buttons.SAVE}
            </BaseButtonPrimary>
          </div>
        </form>
        {displayError ? (
          <span className="absolute bottom-10 left-1/2 w-3/4 -translate-x-1/2 sm:w-96">
            {errorMessage}
          </span>
        ) : null}
      </div>
    </BaseModal>
  );
}
